import { Rng } from './rng';
import { autoSetup, fortify, heal, placeReinforcement, placeSetup, usePower, useHarmony } from './rules/actions';
import { chooseLoot, claimRegions, discardLoot, playLoot } from './rules/loot';
import { attackBoss, chooseBossRegion } from './rules/boss';
import { tradeCards } from './rules/cards';
import { attack, moveIn, react } from './rules/combat';
import { RuleError, type Ctx } from './rules/context';
import { checkVictory } from './rules/missions';
import { endTurn } from './rules/turn';
import { cloneState } from './state';
import type { Action, GameState } from './types';
import { checkAction } from './validate';

export { RuleError };

/**
 * Aplica uma ação e devolve um novo estado (o original não é alterado).
 * Ações inválidas lançam RuleError com um código.
 */
export function applyAction(state: GameState, action: Action): GameState {
  const error = checkAction(state, action);
  if (error) throw new RuleError(error);
  const s = cloneState(state);
  const rng = new Rng(s.rng);
  const ctx: Ctx = { s, rng, events: [] };
  dispatch(ctx, action);
  claimRegions(ctx);
  checkVictory(ctx, action.player);
  s.rng = rng.state;
  s.seq++;
  s.events = ctx.events;
  s.lastAction = { player: action.player, random: rng.draws > 0 };
  return s;
}

/** Versão que não lança: útil para servidor e UI. */
export function tryApplyAction(
  state: GameState,
  action: Action,
): { ok: true; state: GameState } | { ok: false; error: string } {
  try {
    return { ok: true, state: applyAction(state, action) };
  } catch (e) {
    if (e instanceof RuleError) return { ok: false, error: e.code };
    throw e;
  }
}

function dispatch(ctx: Ctx, a: Action): void {
  const { s } = ctx;
  switch (a.type) {
    case 'place':
      if (s.phase === 'setup') placeSetup(ctx, a.player, a.territory, a.count);
      else placeReinforcement(ctx, a.player, a.territory, a.count);
      return;
    case 'autoSetup':
      autoSetup(ctx, a.player);
      return;
    case 'trade':
      tradeCards(ctx, a.player, a.cards);
      return;
    case 'endPhase':
      if (s.phase === 'reinforce') {
        s.phase = 'attack';
        ctx.events.push({ type: 'phase', player: a.player, phase: 'attack' });
      } else if (s.phase === 'attack') {
        s.phase = 'fortify';
        ctx.events.push({ type: 'phase', player: a.player, phase: 'fortify' });
      } else {
        endTurn(ctx);
      }
      return;
    case 'attack':
      attack(ctx, a.player, a.from, a.to, a.untilEnd ?? false);
      return;
    case 'attackBoss':
      attackBoss(ctx, a.player, a.from);
      return;
    case 'moveIn': {
      const p = s.pending;
      if (p?.kind === 'conquest') moveIn(ctx, a.player, p.from, p.to, a.count);
      return;
    }
    case 'react':
      react(ctx, a.player, a.use);
      return;
    case 'discardLoot':
      discardLoot(ctx, a.player, a.kind);
      return;
    case 'fortify':
      fortify(ctx, a.player, a.from, a.to, a.count);
      return;
    case 'power':
      usePower(ctx, a.player, a.target);
      return;
    case 'harmony':
      useHarmony(ctx, a.player);
      return;
    case 'heal':
      heal(ctx, a.player, a.territory);
      return;
    case 'playLoot':
      playLoot(ctx, a);
      return;
    case 'chooseLoot':
      chooseLoot(ctx, a.player, a.kind);
      return;
    case 'chooseBossRegion':
      chooseBossRegion(ctx, a.region);
      return;
  }
}
